import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PortfolioTablePaginationProps {
  currentPage: number;
  totalPages: number;
  pageInputValue: string;
  onPageChange: (page: number) => void;
  onPageInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPageInputBlur: () => void;
  rowsPerPage: number;
  onRowsPerPageChange: (value: string) => void;
  totalCount: number;
}

export const PortfolioTablePagination: React.FC<PortfolioTablePaginationProps> = ({
  currentPage,
  totalPages,
  pageInputValue,
  onPageChange,
  onPageInputChange,
  onPageInputBlur,
  rowsPerPage,
  onRowsPerPageChange,
  totalCount,
}) => {
  return (
    <div className="border-t p-2 flex items-center justify-between bg-muted/20">
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <div className="flex items-center text-sm">
          <span className="mr-2">Page</span>
          <Input
            className="h-8 w-[60px] text-center"
            value={pageInputValue}
            onChange={onPageInputChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newPage = Number(pageInputValue);
                if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
                  onPageChange(newPage);
                }
              }
            }}
            onBlur={onPageInputBlur}
            aria-label="Go to page"
          />
          <span className="mx-2">of {totalPages}</span>
        </div>

        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>

        <div className="flex items-center space-x-4 ml-2">
          <Select value={String(rowsPerPage)} onValueChange={onRowsPerPageChange}>
            <SelectTrigger className="h-8 w-[120px]">
              <SelectValue placeholder={`${rowsPerPage} rows`} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 rows</SelectItem>
              <SelectItem value="50">50 rows</SelectItem>
              <SelectItem value="100">100 rows</SelectItem>
            </SelectContent>
          </Select>

          <div className="text-sm text-muted-foreground">
            {totalCount.toLocaleString()} records
          </div>
        </div>
      </div>
    </div>
  );
};
